import { rememberInStep, type StepState, stepKey, stepState, whenStepChanges } from "./router.ts";

// A trip reopens where it was (DESIGN.md, "The trip's name is its menu; Back stays inside the trip"):
// however I left it (the trip menu, Your trips' card, a notification or a link to the trip itself,
// closing the app), coming back puts it where it was: the view, what was open, the map's camera and
// filters, and each list's scroll and folding. Kept per trip and per account in this browser only: a convenience,
// fine to lose (cleared, or blocked in a private window), and forgotten on signing out.
//
// What's kept is the last step the trip showed: its address and what the step remembers (router.ts),
// written whenever either changes. It's put back when the trip opens on its bare address (`/t/<trip>`)
// at a step it hasn't shown yet; an address that says where to go (`?item=`, `?view=`) opens there,
// and a step already shown (Back, Forward, a reload) is put back as it was, as Back always does.
// The map's filters aren't a step's: they're the trip's, kept as they change and put back whenever the
// trip opens, except on an address that says where to go, which opens with none. Nor is where the map
// is: Back and closing never move it (features/map.md, the founder 2026-10-19), so coming back to the
// map from another view, or reopening the trip, shows it where it was last left (`lastCamera`), never
// where an older step had it.

const PREFIX = "tripmo:";
// What this device keeps per trip, by kind: where the trip was, its map's filters, and Ideas' folds;
// and, with no trip, Your trips' own folds (Recently deleted) and the trips in the order they were last opened here
// (a link shared to Tripmo asks which trip, the last opened first: ShareLink.tsx).
type Kind = "where" | "filters" | "folds" | "opened";
const KINDS: Kind[] = ["where", "filters", "folds", "opened"];
export const keptKey = (kind: Kind, user: string, trip?: string) =>
  `${PREFIX}${kind}:${user}${trip === undefined ? "" : `:${trip}`}`;

// Whose trips are being kept: the person signed in, once a trip has opened.
let account: string | null = null;

type Camera = NonNullable<StepState["camera"]>;
// Where each trip's map was last left, by trip, while the page is open (and kept with where the trip was).
const cameras = new Map<string, Camera>();

// Where this trip's map was last left: as it last came to rest, or as the trip was kept.
export function lastCamera(trip: string): Camera | null {
  return cameras.get(trip) ?? null;
}

// The map came to rest: where it is, the trip's map from now on, whatever step this is.
export function leaveCamera(trip: string, camera: Camera) {
  cameras.set(trip, camera);
}

// Where a trip was: the address's query, where its map was last left, and the step's compared choice and lists.
interface Where {
  search: string;
  state: Pick<StepState, "camera" | "compare" | "lists">;
}

const tripIn = (pathname: string) => {
  const match = pathname.match(/^\/t\/([^/]+)\/?$/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

function keep() {
  const trip = tripIn(location.pathname);
  const step = stepState();
  // Only a step the trip has shown: a step just pushed towards a trip isn't where it was yet.
  if (!account || !trip || !step.shown) return;
  const params = new URLSearchParams(location.search);
  // Trip settings is over the view it was opened from, which is kept already.
  if (params.has("settings")) return;
  // An AI's upload link asks once.
  params.delete("attach");
  const camera = cameras.get(trip) ?? step.camera;
  const where: Where = {
    search: params.toString(),
    state: {
      ...(camera && { camera }),
      ...(step.compare && { compare: step.compare }),
      ...(step.lists && { lists: step.lists }),
    },
  };
  try {
    localStorage.setItem(keptKey("where", account, trip), JSON.stringify(where));
  } catch {
    // Not kept: it still works, it just won't reopen there.
  }
}
whenStepChanges(keep);
addEventListener("popstate", keep);

function kept(user: string, trip: string): Where | null {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(keptKey("where", user, trip)) ?? "null");
    return valid(raw) ? raw : null;
  } catch {
    return null;
  }
}

// What this browser kept may be from an older version of Tripmo, or edited by hand: only what reads as
// it should is put back.
function valid(raw: unknown): raw is Where {
  if (typeof raw !== "object" || raw === null) return false;
  const { search, state } = raw as Record<string, unknown>;
  if (typeof search !== "string" || typeof state !== "object" || state === null) return false;
  const { camera, compare, lists } = state as Record<string, unknown>;
  const cameraOk =
    camera === undefined ||
    (typeof camera === "object" &&
      camera !== null &&
      ["lng", "lat", "zoom"].every((k) => Number.isFinite((camera as Record<string, unknown>)[k])));
  const listsOk = lists === undefined || (typeof lists === "object" && lists !== null && !Array.isArray(lists));
  return cameraOk && listsOk && (compare === undefined || typeof compare === "string");
}

// The trip just opened: on its bare address at a step it hasn't shown, put it where it was. Every step
// the trip shows is marked so (`markShown`), so Back, Forward and a reload find it as it was.
export function reopen(user: string, trip: string) {
  account = user;
  noteOpened(user, trip);
  const step = stepState();
  // Where its map was last left, for a page just loaded: as this step had it (a reload), else as this
  // device kept the trip (a new tab, the app opened again).
  const before = cameras.has(trip) ? null : (step.camera ?? kept(user, trip)?.state.camera);
  if (before) cameras.set(trip, before);
  if (step.shown) return;
  // Somewhere in particular: there, with the map's filters off.
  if (location.search !== "") forget(keptKey("filters", user, trip));
  const where = location.search === "" ? kept(user, trip) : null;
  if (!where) return markShown();
  const query = where.search ? `?${where.search}` : "";
  // The steps that led there aren't in this history: what was open closes in place, inside the trip.
  history.replaceState({ ...where.state, key: stepKey(), shown: true }, "", `${location.pathname}${query}`);
  dispatchEvent(new Event("tripmo:navigate"));
  keep();
}

// The views other than the map, by the address's name for them (TripPage: "options" is Ideas'; any other name
// is the map).
const NOT_THE_MAP = new Set(["days", "ideas", "options", "people", "bookings", "updates"]);

// Whether a trip opening at this address shows its map: on its map view (under whatever is open over it), or, on a
// computer, rating with the map beside the deck; on its bare address, where this device kept it. Asked as the app
// starts, before who I am is known (main.tsx starts loading the map's code with the app's), so a trip kept by any
// account here counts.
export function opensOnMap(trip: string, wide: boolean): boolean {
  let search = location.search;
  if (search === "" && !stepState().shown) {
    try {
      const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i));
      const key = keys.find((k) => k?.startsWith(`${PREFIX}where:`) && k.endsWith(`:${trip}`));
      const raw: unknown = key ? JSON.parse(localStorage.getItem(key) ?? "null") : null;
      if (valid(raw)) search = raw.search;
    } catch {
      // Nothing kept: it opens on the map.
    }
  }
  const params = new URLSearchParams(search);
  if (params.get("settings") === "1") return false;
  if (wide && params.get("rate") === "1") return true;
  return !NOT_THE_MAP.has(params.get("view") ?? "map");
}

// The map's filters as last kept (TripPage checks them against the trip), and keeping them.
export function keptFilters(user: string, trip: string): unknown {
  try {
    return JSON.parse(localStorage.getItem(keptKey("filters", user, trip)) ?? "null");
  } catch {
    return null;
  }
}

export function keepFilters(user: string, trip: string, filters: unknown) {
  try {
    localStorage.setItem(keptKey("filters", user, trip), JSON.stringify(filters));
  } catch {
    // Not kept: they still filter for now.
  }
}

function forget(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage blocked: nothing was kept.
  }
}

export function markShown() {
  if (!stepState().shown) rememberInStep({ shown: true });
}

// A notification or link to the trip itself, while that trip is open: it stays where it is.
export function alreadyThere(path: string): boolean {
  const url = new URL(path, location.origin);
  const trip = tripIn(url.pathname);
  return trip !== null && url.search === "" && trip === tripIn(location.pathname);
}

// Signing out: what this browser kept of my trips goes (every account's, and from before it was kept
// per account).
export function forgetTrips() {
  cameras.clear();
  try {
    const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i));
    for (const key of keys)
      if (key && KINDS.some((k) => key.startsWith(`${PREFIX}${k}:`))) localStorage.removeItem(key);
  } catch {
    // Storage blocked: nothing was kept.
  }
}

// The trips opened on this device, the last opened first, by their ids.
export function openedOrder(user: string): string[] {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(keptKey("opened", user)) ?? "[]");
    return Array.isArray(raw) ? raw.filter((t): t is string => typeof t === "string") : [];
  } catch {
    return [];
  }
}

function noteOpened(user: string, trip: string) {
  const order = [trip, ...openedOrder(user).filter((t) => t !== trip)].slice(0, 50);
  try {
    localStorage.setItem(keptKey("opened", user), JSON.stringify(order));
  } catch {
    // Not kept: shared links list the trips in their own order.
  }
}
